import { appleProvider } from '@/features/Auth/providers/apple'
import { customTokenProvider } from '@/features/Auth/providers/customToken'
import { emailProvider } from '@/features/Auth/providers/email'
import { facebookProvider } from '@/features/Auth/providers/facebook'
import { googleProvider } from '@/features/Auth/providers/google'
import { linkedinProvider } from '@/features/Auth/providers/linkedin'
import { CredentialProvider, FailureReason, ProviderFailure, ProviderId } from '@/features/Auth/providers/types'
import { AuthEvent } from '@/features/Auth/machine/states'

import { exchange } from '@/features/Auth/session/exchange'

const providers: Record<ProviderId, CredentialProvider<any>> = {
  apple: appleProvider,
  customToken: customTokenProvider,
  email: emailProvider,
  facebook: facebookProvider,
  google: googleProvider,
  linkedin: linkedinProvider
}

export type Emit = (event: AuthEvent) => void

/**
 * A provider knows why it failed and can say so; anything else is unexplained. The message
 * travels with the event so the one place that reports failures can be specific without
 * keeping a table of every provider's error codes — which is what the login screen used to
 * do, for one provider, in its own copy.
 */
function failureFrom(error: unknown): { reason: FailureReason; message?: string } {
  return error instanceof ProviderFailure ? { reason: error.reason, message: error.message } : { reason: 'rejected' }
}

/**
 * Signing in, for every provider.
 *
 * There is no separate social path and no separate e-mail path: the provider decides how a
 * credential is obtained, `exchange` decides how it is redeemed, and the machine decides what
 * that means. Adding a provider means adding a provider, and moving the exchange to the
 * backend means changing `exchange` — neither touches this. See EPIC-005.
 *
 * The caller is handed events rather than being navigated. Deciding where someone goes is the
 * bridge's job; a service that navigates is what broke the dependency direction both
 * TEMPLATE.md files require.
 */
export async function signIn<Id extends ProviderId>(
  provider: Id,
  input: Parameters<(typeof providers)[Id]['authenticate']>[0],
  emit: Emit
): Promise<void> {
  emit({ type: 'signInRequested', provider })

  let credential

  try {
    credential = await providers[provider].authenticate(input)
  } catch (error) {
    emit({ type: 'failed', ...failureFrom(error) })
    return
  }

  emit({ type: 'credentialObtained' })

  try {
    const session = await exchange(credential)

    emit({ type: 'sessionEstablished', uid: session.uid, extras: session.extras })
  } catch (error) {
    emit({ type: 'failed', ...failureFrom(error) })
  }
}
